import { useEffect, useState } from 'react';

interface Shape {
  id: number;
  type: 'cube' | 'pyramid' | 'sphere' | 'ring';
  x: number;
  y: number;
  size: number;
  rotationSpeed: number;
  delay: number;
  gradient: string;
}

const FloatingShapes = () => {
  const [shapes] = useState<Shape[]>(() => {
    const gradients = [
      'from-purple-500 to-pink-500',
      'from-blue-500 to-cyan-500',
      'from-emerald-500 to-teal-500',
      'from-orange-500 to-yellow-500',
    ];
    const types: Shape['type'][] = ['cube', 'pyramid', 'sphere', 'ring'];
    
    return Array.from({ length: 6 }, (_, i) => ({
      id: i,
      type: types[i % types.length],
      x: 10 + Math.random() * 80,
      y: 10 + Math.random() * 80,
      size: 40 + Math.random() * 40,
      rotationSpeed: 8 + Math.random() * 8,
      delay: Math.random() * 3,
      gradient: gradients[i % gradients.length],
    }));
  });

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {shapes.map((shape) => (
        <div
          key={shape.id}
          className="absolute preserve-3d"
          style={{
            left: `${shape.x}%`,
            top: `${shape.y}%`,
            width: shape.size,
            height: shape.size,
            animation: `float3d ${shape.rotationSpeed}s ease-in-out infinite, rotate3d ${shape.rotationSpeed * 2}s linear infinite`,
            animationDelay: `${shape.delay}s`,
          }}
        >
          {shape.type === 'cube' && <Cube gradient={shape.gradient} size={shape.size} />}
          {shape.type === 'pyramid' && <Pyramid gradient={shape.gradient} size={shape.size} />}
          {shape.type === 'sphere' && <Sphere gradient={shape.gradient} size={shape.size} />}
          {shape.type === 'ring' && <Ring gradient={shape.gradient} size={shape.size} />}
        </div>
      ))}
    </div>
  );
};

const Cube = ({ gradient, size }: { gradient: string; size: number }) => (
  <div 
    className="relative preserve-3d"
    style={{ 
      width: size, 
      height: size,
      transformStyle: 'preserve-3d',
    }}
  >
    {/* Front */}
    <div 
      className={`absolute bg-gradient-to-br ${gradient} opacity-40 backdrop-blur-sm border border-white/20`}
      style={{ 
        width: size, 
        height: size,
        transform: `translateZ(${size/2}px)`,
      }}
    />
    {/* Back */}
    <div 
      className={`absolute bg-gradient-to-br ${gradient} opacity-30 backdrop-blur-sm border border-white/20`}
      style={{ 
        width: size, 
        height: size,
        transform: `translateZ(-${size/2}px) rotateY(180deg)`,
      }}
    />
    {/* Left */}
    <div 
      className={`absolute bg-gradient-to-br ${gradient} opacity-35 backdrop-blur-sm border border-white/20`}
      style={{ 
        width: size, 
        height: size,
        transform: `translateX(-${size/2}px) rotateY(-90deg)`,
      }}
    />
    {/* Right */}
    <div 
      className={`absolute bg-gradient-to-br ${gradient} opacity-35 backdrop-blur-sm border border-white/20`}
      style={{ 
        width: size, 
        height: size,
        transform: `translateX(${size/2}px) rotateY(90deg)`,
      }}
    />
    {/* Top */}
    <div 
      className={`absolute bg-gradient-to-br ${gradient} opacity-45 backdrop-blur-sm border border-white/20`}
      style={{ 
        width: size, 
        height: size,
        transform: `translateY(-${size/2}px) rotateX(90deg)`,
      }}
    />
    {/* Bottom */}
    <div 
      className={`absolute bg-gradient-to-br ${gradient} opacity-25 backdrop-blur-sm border border-white/20`}
      style={{ 
        width: size, 
        height: size,
        transform: `translateY(${size/2}px) rotateX(-90deg)`,
      }}
    />
  </div>
);

const Pyramid = ({ gradient, size }: { gradient: string; size: number }) => (
  <div 
    className="relative preserve-3d"
    style={{ 
      width: size, 
      height: size,
      transformStyle: 'preserve-3d',
    }}
  >
    {/* Base */}
    <div 
      className={`absolute bg-gradient-to-br ${gradient} opacity-30`}
      style={{ 
        width: size, 
        height: size,
        transform: `translateY(${size/2}px) rotateX(-90deg)`,
        clipPath: 'polygon(50% 50%, 0% 100%, 100% 100%)',
      }}
    />
    {/* Front face */}
    <div 
      className={`absolute bg-gradient-to-t ${gradient} opacity-40 border-l border-r border-white/20`}
      style={{ 
        width: size, 
        height: size,
        transform: `rotateX(-30deg)`,
        clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
      }}
    />
  </div>
);

const Sphere = ({ gradient, size }: { gradient: string; size: number }) => (
  <div 
    className={`rounded-full bg-gradient-to-br ${gradient} opacity-40 shadow-2xl`}
    style={{ 
      width: size, 
      height: size,
      boxShadow: `inset -${size/4}px -${size/4}px ${size/2}px rgba(0,0,0,0.3), inset ${size/6}px ${size/6}px ${size/3}px rgba(255,255,255,0.2)`,
    }}
  />
);

const Ring = ({ gradient, size }: { gradient: string; size: number }) => (
  <div 
    className={`rounded-full border-4 bg-transparent opacity-50`}
    style={{ 
      width: size, 
      height: size,
      borderColor: 'transparent',
      borderTopColor: 'rgba(168, 85, 247, 0.6)',
      borderLeftColor: 'rgba(236, 72, 153, 0.4)',
      animation: 'spin 3s linear infinite',
    }}
  >
    <div 
      className="absolute inset-2 rounded-full border-2"
      style={{ 
        borderColor: 'transparent',
        borderBottomColor: 'rgba(59, 130, 246, 0.5)',
        borderRightColor: 'rgba(34, 211, 238, 0.3)',
        animation: 'spin 2s linear infinite reverse',
      }}
    />
  </div>
);

export default FloatingShapes;
